// 游戏结束判断逻辑
import type { ChessType, ChessPosition } from "@/store/game"
import { BOARD_SIZE } from "@/store/game"
import {
    GAME_END_FALLBACK_MAX_STEPS,
    GAME_END_SCORE_THRESHOLD,
    GAME_END_SCORE_DIFF_THRESHOLD,
    GAME_END_CONSECUTIVE_DIFF_THRESHOLD,
    GAME_END_CONSECUTIVE_ROUNDS_THRESHOLD,
} from "@/const/game"
import { calculateScore } from "./score"

/**
 * 获取所有可以落子的空位置
 */
function getEmptyPositions(board: ChessType[][]): ChessPosition[] {
    const positions: ChessPosition[] = []
    for (let row = 0; row < BOARD_SIZE; row++) {
        for (let col = 0; col < BOARD_SIZE; col++) {
            if (board[row][col] === null) {
                positions.push({ row, col })
            }
        }
    }
    return positions
}

/**
 * 检查当前玩家是否可以下棋
 * @param board 棋盘状态
 * @param _currentPlayer 当前玩家（预留，当前规则下暂未使用）
 */
function hasValidMove(board: ChessType[][], _currentPlayer: boolean): boolean {
    // 检查是否还有空位
    const emptyPositions = getEmptyPositions(board)
    if (emptyPositions.length === 0) {
        return false // 棋盘满了
    }

    // 在当前规则下（无自杀限制），只要有空位就可以下
    // 所以只要有空位，就返回 true
    return true
}

/**
 * 规则 A：无法落子结束（最高优先级）
 * @param board 棋盘状态
 * @param currentPlayer 当前玩家（true=黑棋，false=白棋）
 * @returns 如果无法落子，返回 true（游戏结束）
 */
function checkRuleA_UnableToMove(
    board: ChessType[][],
    currentPlayer: boolean
): boolean {
    // 情况1：棋盘已满
    const emptyPositions = getEmptyPositions(board)
    if (emptyPositions.length === 0) {
        return true
    }

    // 情况2：当前玩家无法下棋，游戏结束
    const currentPlayerCanMove = hasValidMove(board, currentPlayer)
    if (!currentPlayerCanMove) {
        return true
    }

    return false
}

/**
 * 从历史记录数组末尾统计连续达到分差要求的步数
 * @param history 历史记录数组，每个元素代表一步是否达到分差要求
 * @returns 连续达到分差要求的步数（从数组末尾开始统计）
 */
function countConsecutiveLargeDiffSteps(history: boolean[]): number {
    if (history.length === 0) {
        return 0
    }

    // 从数组末尾开始，统计连续为 true 的元素数量
    let count = 0
    for (let i = history.length - 1; i >= 0; i--) {
        if (history[i]) {
            count++
        } else {
            // 遇到 false 就停止统计（因为要求是"连续"）
            break
        }
    }
    return count
}

/**
 * 规则 C：纯 TS 实现的兜底判断逻辑
 *
 * 判断规则：
 * 1. 步数限制：超过配置的最大步数（默认 90）强制结束
 * 2. 某一方得分达到45
 * 3. 分差大于等于25
 * 4. 连续N步分差大于等于20（N = 2*回合数限制，每一手为一次）
 *
 * @param board 棋盘状态
 * @param _currentPlayer 当前玩家（true=黑棋，false=白棋）（预留，暂未使用）
 * @param currentStep 当前步数
 * @param blackCaptures 黑棋提子数
 * @param whiteCaptures 白棋提子数
 * @param consecutiveLargeDiffHistory 连续大分差历史记录（每个元素代表一步是否达到分差要求）
 * @returns 返回 { shouldEnd: boolean, updatedHistory: boolean[] } 是否应该结束游戏和更新后的历史记录
 */
function checkRuleC_TsFallback(
    board: ChessType[][],
    _currentPlayer: boolean,
    currentStep: number,
    blackCaptures: number,
    whiteCaptures: number,
    consecutiveLargeDiffHistory: boolean[]
): { shouldEnd: boolean; updatedHistory: boolean[] } {
    // 预留参数，当前未使用
    void _currentPlayer

    // 步数限制 - 超过最大步数强制结束
    // 9路棋盘总共81个格子，考虑到技能可能增加棋子，90步是一个合理的上限
    if (currentStep >= GAME_END_FALLBACK_MAX_STEPS) {
        // 计算当前步的分差，判断是否达到阈值
        const { diff } = calculateScore(board, blackCaptures, whiteCaptures)
        const scoreDiffAbs = Math.abs(diff)
        const currentStepReachedThreshold =
            scoreDiffAbs >= GAME_END_CONSECUTIVE_DIFF_THRESHOLD
        const updatedHistory = [
            ...consecutiveLargeDiffHistory,
            currentStepReachedThreshold,
        ]
        return { shouldEnd: true, updatedHistory }
    }

    // 计算当前得分
    const {
        blackScore,
        whiteScore,
        diff: scoreDiff,
    } = calculateScore(board, blackCaptures, whiteCaptures)

    // 规则1：某一方得分达到阈值
    if (
        blackScore >= GAME_END_SCORE_THRESHOLD ||
        whiteScore >= GAME_END_SCORE_THRESHOLD
    ) {
        // 计算当前步的分差，判断是否达到阈值
        const scoreDiffAbs = Math.abs(scoreDiff)
        const currentStepReachedThreshold =
            scoreDiffAbs >= GAME_END_CONSECUTIVE_DIFF_THRESHOLD
        const updatedHistory = [
            ...consecutiveLargeDiffHistory,
            currentStepReachedThreshold,
        ]
        return { shouldEnd: true, updatedHistory }
    }

    // 规则2：分差大于等于阈值
    const scoreDiffAbs = Math.abs(scoreDiff)
    if (scoreDiffAbs >= GAME_END_SCORE_DIFF_THRESHOLD) {
        // 计算当前步的分差，判断是否达到阈值
        const currentStepReachedThreshold =
            scoreDiffAbs >= GAME_END_CONSECUTIVE_DIFF_THRESHOLD
        const updatedHistory = [
            ...consecutiveLargeDiffHistory,
            currentStepReachedThreshold,
        ]
        return { shouldEnd: true, updatedHistory }
    }

    // 规则3：连续步数分差大于等于阈值
    // 判断当前步是否达到分差要求
    const currentStepReachedThreshold =
        scoreDiffAbs >= GAME_END_CONSECUTIVE_DIFF_THRESHOLD
    // 将当前步的状态添加到历史记录中
    const updatedHistory = [
        ...consecutiveLargeDiffHistory,
        currentStepReachedThreshold,
    ]

    // 从数组末尾统计连续达到分差要求的步数
    const consecutiveSteps = countConsecutiveLargeDiffSteps(updatedHistory)

    if (consecutiveSteps >= GAME_END_CONSECUTIVE_ROUNDS_THRESHOLD) {
        return { shouldEnd: true, updatedHistory }
    }

    // 所有规则都不满足，游戏继续
    return { shouldEnd: false, updatedHistory }
}

/**
 * 仅将本步是否达到大分差追加到连续大分差历史（不参与是否结束的判断）
 * 用于黑棋刚下完时：只做规则 A + 更新历史，不跑规则 C 的结束逻辑
 */
function appendConsecutiveHistoryStep(
    board: ChessType[][],
    blackCaptures: number,
    whiteCaptures: number,
    consecutiveLargeDiffHistory: boolean[]
): boolean[] {
    const { diff } = calculateScore(board, blackCaptures, whiteCaptures)
    const currentStepReachedThreshold =
        Math.abs(diff) >= GAME_END_CONSECUTIVE_DIFF_THRESHOLD
    return [...consecutiveLargeDiffHistory, currentStepReachedThreshold]
}

/**
 * 检查游戏是否应该结束（主函数）
 * 按照规则优先级：规则A > 规则B > 规则C
 *
 * @param board 棋盘状态
 * @param currentPlayer 当前玩家（下一手方，true=黑棋，false=白棋）
 * @param currentStep 当前步数
 * @param blackCaptures 黑棋提子数
 * @param whiteCaptures 白棋提子数
 * @param consecutiveLargeDiffHistory 连续大分差历史记录
 * @param skipScoreRules 为 true 时只做规则 A + 更新历史，不跑规则 C（黑棋刚下完时传 true，避免少白棋一手就判分）
 * @returns { shouldEnd: boolean; updatedHistory: boolean[] }
 */
export function shouldGameEnd(
    board: ChessType[][],
    currentPlayer: boolean,
    currentStep: number,
    blackCaptures: number,
    whiteCaptures: number,
    consecutiveLargeDiffHistory: boolean[],
    skipScoreRules?: boolean
): { shouldEnd: boolean; updatedHistory: boolean[] } {
    // 规则 A：无法落子结束（黑/白下完都可判，与先后手无关）
    if (checkRuleA_UnableToMove(board, currentPlayer)) {
        const updatedHistory = appendConsecutiveHistoryStep(
            board,
            blackCaptures,
            whiteCaptures,
            consecutiveLargeDiffHistory
        )
        return { shouldEnd: true, updatedHistory }
    }

    // 黑棋刚下完：只更新连续大分差历史，不跑规则 C（不判步数/得分/分差是否结束）
    if (skipScoreRules) {
        const updatedHistory = appendConsecutiveHistoryStep(
            board,
            blackCaptures,
            whiteCaptures,
            consecutiveLargeDiffHistory
        )
        return { shouldEnd: false, updatedHistory }
    }

    // 规则 C：步数/得分/分差/连续大分差（仅在白棋也下完时参与结束判断）
    const ruleCResult = checkRuleC_TsFallback(
        board,
        currentPlayer,
        currentStep,
        blackCaptures,
        whiteCaptures,
        consecutiveLargeDiffHistory
    )

    return ruleCResult
}
